import { useState, useCallback } from 'react';
import { Check, AlertTriangle, FlaskConical, Shield } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { AnalyticalBalance, MortarAndPestle } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Laboratory Safety',
    content: 'This experiment uses hydrochloric acid (HCl), which is corrosive and can cause burns. Always wear safety goggles, gloves, and a lab coat. Work in a well-ventilated area. Know the location of the eyewash station and safety shower.',
    formulas: ['HCl (aq) -> H+ + Cl-']
  },
  2: {
    title: 'Antacid Active Ingredients',
    content: 'Commercial antacids contain bases that neutralize stomach acid. Common active ingredients include calcium carbonate (Tums), magnesium hydroxide (Milk of Magnesia), and aluminum hydroxide. Each has different neutralizing capacities measured in milliequivalents (mEq).',
    formulas: ['CaCO₃ + 2HCl → CaCl₂ + H₂O + CO₂', 'Mg(OH)₂ + 2HCl → MgCl₂ + 2H₂O']
  },
  3: {
    title: 'Mass Measurement',
    content: 'Accurate mass measurement is critical for calculating the neutralizing capacity. Use an analytical balance with 0.0001 g precision. Allow the balance to stabilize before recording. Record the mass to four decimal places.',
    formulas: ['Precision = +/- 0.0001 g', 'Mass tablet = reading - tare']
  },
  4: {
    title: 'Grinding the Tablet',
    content: 'Crushing the tablet increases surface area for faster, more complete reaction with HCl. Use a mortar and pestle with a circular grinding motion. Continue until a fine, uniform powder is obtained with no visible chunks.',
    formulas: ['Surface area increase -> faster reaction rate', 'Rate proportional to SA']
  }
};

// Antacid types with properties
const ANTACID_TYPES = [
  { id: 'tums', name: 'Tums', formula: 'CaCO₃', mEq: 10, color: '#F5F5F5', description: 'Calcium carbonate' },
  { id: 'rolaids', name: 'Rolaids', formula: 'NaAl(OH)₂CO₃', mEq: 7.5, color: '#E8E8E8', description: 'Sodium dihydroxy aluminum carbonate' },
  { id: 'magnesia', name: 'Milk of Magnesia', formula: 'Mg(OH)₂', mEq: 12, color: '#F0F8FF', description: 'Magnesium hydroxide' },
  { id: 'eno', name: 'Eno', formula: 'NaHCO₃', mEq: 8, color: '#FFFFFF', description: 'Sodium bicarbonate' },
  { id: 'brioschi', name: 'Brioschi', formula: 'NaHCO₃', mEq: 8, color: '#FFFFFF', description: 'Sodium bicarbonate' }
];

export default function Part1Introduction() {
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [selectedAntacid, setSelectedAntacid] = useState<typeof ANTACID_TYPES[0] | null>(null);
  const [tabletWeighed, setTabletWeighed] = useState(false);
  const [tabletCrushed, setTabletCrushed] = useState(false);
  const [tabletMass, setTabletMass] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [isGrinding, setIsGrinding] = useState(false);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [safetyAcknowledged, selectedAntacid !== null, tabletWeighed, tabletCrushed].filter(Boolean).length;

  const handleSafetyAcknowledge = useCallback(() => {
    if (!safetyAcknowledged) {
      setSafetyAcknowledged(true);
      setCurrentStep(2);
    }
  }, [safetyAcknowledged]);

  const handleSelectAntacid = useCallback((antacid: typeof ANTACID_TYPES[0]) => {
    if (safetyAcknowledged && !selectedAntacid) {
      setSelectedAntacid(antacid);
      setCurrentStep(3);
    }
  }, [safetyAcknowledged, selectedAntacid]);

  const handleWeighTablet = useCallback(() => {
    if (selectedAntacid && !tabletWeighed) {
      // Generate a realistic mass based on antacid type
      const baseMass = selectedAntacid.id === 'tums' ? 1.3 : selectedAntacid.id === 'rolaids' ? 1.1 : 0.9;
      const mass = baseMass + (Math.random() * 0.2 - 0.1);
      setTabletMass(parseFloat(mass.toFixed(4)));
      setTabletWeighed(true);
      setCurrentStep(4);
    }
  }, [selectedAntacid, tabletWeighed]);

  const handleCrushTablet = useCallback(() => {
    if (tabletWeighed && !tabletCrushed) {
      setIsGrinding(true);
      setTimeout(() => {
        setIsGrinding(false);
        setTabletCrushed(true);
      }, 2000);
    }
  }, [tabletWeighed, tabletCrushed]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part I: Introduction</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Safety Review', hint: 'Acknowledge safety procedures', done: safetyAcknowledged },
            { id: 2, title: 'Select Antacid', hint: 'Choose an antacid tablet', done: selectedAntacid !== null },
            { id: 3, title: 'Weigh Tablet', hint: 'Record the tablet mass', done: tabletWeighed },
            { id: 4, title: 'Crush Tablet', hint: 'Grind to fine powder', done: tabletCrushed }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Antacid Info */}
        {selectedAntacid && (
          <div className="antacid-info">
            <h4>Selected Antacid</h4>
            <div className="info-row">
              <span>Type:</span>
              <span className="value">{selectedAntacid.name}</span>
            </div>
            <div className="info-row">
              <span>Formula:</span>
              <span className="value">{selectedAntacid.formula}</span>
            </div>
            <div className="info-row">
              <span>Expected mEq:</span>
              <span className="value">~{selectedAntacid.mEq} mEq HCl</span>
            </div>
            {tabletWeighed && (
              <div className="info-row highlight">
                <span>Mass:</span>
                <span className="value">{tabletMass.toFixed(4)} g</span>
              </div>
            )}
          </div>
        )}
      </div>

      <LabBench
        experimentName="Antacid Tablet Analysis - Introduction"
        completedCount={completedSteps}
        totalSteps={4}
        equipment={['Antacid tablets', 'Analytical balance', 'Mortar and pestle']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="intro-workspace">
          {/* Safety Section */}
          {currentStep === 1 && !safetyAcknowledged && (
            <div className="safety-section" onClick={handleSafetyAcknowledge}>
              <div className="safety-card">
                <Shield size={48} className="safety-icon" />
                <h3>Safety Precautions</h3>
                <ul className="safety-list">
                  <li><AlertTriangle size={14} /> Wear safety goggles at all times</li>
                  <li><AlertTriangle size={14} /> HCl is corrosive - avoid skin contact</li>
                  <li><AlertTriangle size={14} /> Work in a well-ventilated area</li>
                  <li><AlertTriangle size={14} /> Handle glassware carefully</li>
                </ul>
                <button className="acknowledge-btn">
                  Click to Acknowledge Safety Procedures
                </button>
              </div>
            </div>
          )}

          {/* Antacid Selection */}
          {safetyAcknowledged && currentStep === 2 && !selectedAntacid && (
            <div className="antacid-selection">
              <h3>Select an Antacid Tablet</h3>
              <div className="antacid-options">
                {ANTACID_TYPES.map((antacid) => (
                  <div
                    key={antacid.id}
                    className="antacid-option"
                    onClick={() => handleSelectAntacid(antacid)}
                  >
                    <svg viewBox="0 0 60 60" width={80} height={80}>
                      {/* Tablet shape */}
                      <ellipse cx="30" cy="30" rx="25" ry="12" fill={antacid.color} stroke="#999" strokeWidth="1" />
                      <ellipse cx="30" cy="28" rx="22" ry="10" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
                    </svg>
                    <span className="antacid-name">{antacid.name}</span>
                    <span className="antacid-formula">{antacid.formula}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Weighing Station */}
          {selectedAntacid && currentStep === 3 && !tabletWeighed && (
            <div className="weighing-section">
              <div
                className="equipment-station clickable"
                onClick={handleWeighTablet}
              >
                <AnalyticalBalance
                  mass={0}
                  tared={true}
                  hasSample={false}
                  stable={true}
                />
                <span className="equipment-label">Click to weigh tablet</span>
              </div>
              <div className="tablet-display">
                <svg viewBox="0 0 60 60" width={60} height={60}>
                  <ellipse cx="30" cy="30" rx="25" ry="12" fill={selectedAntacid.color} stroke="#999" strokeWidth="1" />
                </svg>
                <span>{selectedAntacid.name} tablet</span>
              </div>
            </div>
          )}

          {/* Crushing Station */}
          {tabletWeighed && currentStep === 4 && (
            <div className="crushing-section">
              <div
                className={`equipment-station ${!tabletCrushed ? 'clickable' : ''}`}
                onClick={handleCrushTablet}
              >
                <MortarAndPestle
                  hasContent={true}
                  grinding={isGrinding}
                  contentColor={selectedAntacid?.color || '#F5F5DC'}
                  contentLevel={tabletCrushed ? 30 : 70}
                />
                <span className="equipment-label">
                  {tabletCrushed ? 'Tablet crushed to fine powder' : 'Click to crush tablet'}
                </span>
              </div>
            </div>
          )}

          {/* Completion Message */}
          {tabletCrushed && (
            <div className="status-message success">
              <Check size={20} />
              Introduction complete! Tablet mass: {tabletMass.toFixed(4)} g. Ready for sample preparation.
            </div>
          )}

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Antacid Analysis</h4>
            <p>
              Antacids neutralize stomach acid (HCl). The neutralizing capacity is measured
              in milliequivalents (mEq) of HCl neutralized. By direct titration with standardized
              0.2 M HCl, we can determine how much acid the antacid neutralizes.
            </p>
            <p>
              Reaction: Antacid + HCl → Salt + H₂O (+ CO₂ for carbonates)
            </p>
          </div>
        </div>

        <style>{`
          .intro-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .safety-section {
            cursor: pointer;
          }

          .safety-card {
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 191, 36, 0.1));
            border: 2px solid #FBBF24;
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            max-width: 400px;
          }

          .safety-icon {
            color: #FBBF24;
            margin-bottom: 12px;
          }

          .safety-card h3 {
            margin: 0 0 16px 0;
            color: #FBBF24;
          }

          .safety-list {
            list-style: none;
            padding: 0;
            margin: 0 0 16px 0;
            text-align: left;
          }

          .safety-list li {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .safety-list li svg {
            color: #FBBF24;
            flex-shrink: 0;
          }

          .acknowledge-btn {
            background: #FBBF24;
            color: #1a1a1a;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .acknowledge-btn:hover {
            background: #F59E0B;
          }

          .antacid-selection h3 {
            margin: 0 0 20px 0;
            color: var(--text-primary);
          }

          .antacid-options {
            display: flex;
            gap: 24px;
          }

          .antacid-option {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 16px;
            background: var(--bg-elevated);
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s;
            border: 2px solid transparent;
          }

          .antacid-option:hover {
            background: rgba(0, 168, 107, 0.1);
            border-color: var(--secondary);
          }

          .antacid-name {
            font-weight: 600;
            color: var(--text-primary);
          }

          .antacid-formula {
            font-size: 12px;
            color: var(--text-secondary);
            font-family: serif;
          }

          .weighing-section, .crushing-section {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 12px;
            color: var(--text-secondary);
            text-align: center;
          }

          .tablet-display {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .tablet-display span {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .antacid-info {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            margin-top: 16px;
          }

          .antacid-info h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 13px;
          }

          .info-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .info-row.highlight {
            background: rgba(0, 168, 107, 0.1);
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
          }

          .info-row.highlight .value {
            color: var(--secondary);
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box p:last-child {
            margin-bottom: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp10"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
